import { Alert, Box, Typography, Button, Link as JoyLink } from '@mui/joy';
import LinkIcon from '@mui/icons-material/Link';
import WorkIcon from '@mui/icons-material/Work';
import { useNavigate } from 'react-router';
import { useQuery } from '@apollo/client';
import { useState, useEffect, useMemo } from 'react';
import { getProvider } from './providers';
import { getProviderQueries } from './providerQueryMap';
import useTracking from '../useTracking';

const DISMISS_CONNECT = 'ats-banner-dismissed-connect';
const DISMISS_IMPORT = 'ats-banner-dismissed-import';

const ATSConnectBanner = ({ providerId, clientId, onImportStepVisible }) => {
  const navigate = useNavigate();
  const { trackImportJobButton } = useTracking();
  const provider = providerId ? getProvider(providerId) : null;
  const pq = provider ? getProviderQueries(providerId) : null;

  const [connectDismissed, setConnectDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_CONNECT) === '1'
  );
  const [importDismissed, setImportDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_IMPORT) === '1'
  );

  const { data: statusData, loading: statusLoading } = useQuery(pq?.GET_STATUS, {
    skip: !pq,
    fetchPolicy: 'network-only',
  });

  const isConnected = pq ? statusData?.[pq.statusField]?.enabled : false;

  const { data: jobsData, loading: jobsLoading } = useQuery(pq?.LIST_JOBS, {
    skip: !pq || !isConnected,
    fetchPolicy: 'network-only',
  });

  const atsJobs = (pq && jobsData?.[pq.jobsField]) ?? [];
  const linkedJob = atsJobs.find(j => j.linkedCavalryJobId);

  const step = useMemo(() => {
    if (!provider || statusLoading || (isConnected && jobsLoading)) return null;
    if (!isConnected && !connectDismissed) return 'connect';
    if (isConnected && !linkedJob && !importDismissed) return 'import';
    return null;
  }, [provider, statusLoading, isConnected, jobsLoading, connectDismissed, linkedJob, importDismissed]);

  useEffect(() => {
    onImportStepVisible?.(step === 'import');
  }, [step, onImportStepVisible]);

  if (!step) return null;

  let icon, title, subtitle, ctaLabel, ctaRoute, dismiss;
  if (step === 'connect') {
    icon = <LinkIcon />;
    title = 'Next step \u2014 connect your ATS';
    subtitle = `Link your ${provider.name} account to start importing jobs and candidates.`;
    ctaLabel = `Connect ${provider.name}`;
    ctaRoute = '/onboarding/connect-ats';
    dismiss = () => { sessionStorage.setItem(DISMISS_CONNECT, '1'); setConnectDismissed(true); };
  } else {
    icon = <WorkIcon />;
    title = 'Next step \u2014 import a job';
    subtitle = `${provider.name} is connected. Import a job to continue setting up your workflow.`;
    ctaLabel = 'Import a job';
    ctaRoute = '/onboarding/import-job';
    dismiss = () => { sessionStorage.setItem(DISMISS_IMPORT, '1'); setImportDismissed(true); };
  }

  const handleCtaClick = async () => {
    if (step === 'import') {
      await trackImportJobButton(clientId);
    }
    navigate(ctaRoute);
  };

  return (
    <Alert
      color="primary"
      variant="soft"
      startDecorator={icon}
      sx={{ mb: 2 }}
      endDecorator={
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            size="sm"
            variant="solid"
            color="primary"
            onClick={handleCtaClick}
          >
            {ctaLabel}
          </Button>
          <JoyLink
            level="body-sm"
            color="neutral"
            component="button"
            onClick={dismiss}
          >
            Remind me later
          </JoyLink>
        </Box>
      }
    >
      <Box>
        <Typography level="title-sm">{title}</Typography>
        <Typography level="body-xs">{subtitle}</Typography>
      </Box>
    </Alert>
  );
};

export default ATSConnectBanner;
